import React from 'react';
import { useTranslation } from 'react-i18next';
import { Interview, InterviewResponse } from '../types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { 
  StarIcon, 
  TrendingUpIcon, 
  TrendingDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon
} from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface InterviewSummaryProps {
  interview: Interview;
}

export function InterviewSummary({ interview }: InterviewSummaryProps) {
  const { t } = useTranslation();
  const responses = interview.responses || [];

  // Calculate average score
  const averageScore = responses.reduce((acc, response) => {
    return acc + (response.feedback?.score || 0);
  }, 0) / (responses.length || 1);

  // Prepare data for the chart
  const chartData = {
    labels: responses.map((_, index) => `Q${index + 1}`),
    datasets: [
      {
        label: t('interview.summary.questionScores'),
        data: responses.map(response => response.feedback?.score || 0),
        backgroundColor: 'rgba(99, 102, 241, 0.5)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const
      },
      title: {
        display: true,
        text: t('interview.summary.scoresByQuestion')
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: number) => `${value}%`
        }
      }
    }
  };

  // Collect all strengths and improvements
  const allStrengths = responses.flatMap(r => r.feedback?.strengths || []);
  const allImprovements = responses.flatMap(r => r.feedback?.improvements || []);

  // Get unique strengths and improvements
  const uniqueStrengths = Array.from(new Set(allStrengths));
  const uniqueImprovements = Array.from(new Set(allImprovements));

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('interview.summary.title')}
        </h2>
        <p className="text-gray-600">
          {t('interview.summary.jobTitle')}: {interview.jobTitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-50 to-white rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <StarIcon className="h-8 w-8 text-indigo-600" />
          </div>
          <div className={`text-4xl font-bold ${getScoreColor(averageScore)}`}>
            {Math.round(averageScore)}%
          </div>
          <div className="text-sm font-medium text-gray-600 mt-2">
            {t('interview.summary.overallScore')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <div className="text-4xl font-bold text-green-600">
            {uniqueStrengths.length}
          </div>
          <div className="text-sm font-medium text-gray-600 mt-2">
            {t('interview.summary.keyStrengthsCount')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-white rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingUpIcon className="h-8 w-8 text-yellow-600" />
          </div>
          <div className="text-4xl font-bold text-yellow-600">
            {uniqueImprovements.length}
          </div>
          <div className="text-sm font-medium text-gray-600 mt-2">
            {t('interview.summary.improvementAreasCount')}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('interview.summary.performanceAnalysis')}
        </h3>
        <div className="h-64">
          <Bar options={chartOptions} data={chartData} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-green-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <TrendingUpIcon className="h-5 w-5" />
            {t('interview.summary.keyStrengths')}
          </h3>
          <ul className="space-y-3">
            {uniqueStrengths.map((strength, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{strength}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-yellow-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
            <TrendingDownIcon className="h-5 w-5" />
            {t('interview.summary.areasForImprovement')}
          </h3>
          <ul className="space-y-3">
            {uniqueImprovements.map((improvement, index) => (
              <li key={index} className="flex items-start gap-2">
                <XCircleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{improvement}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          {t('interview.summary.detailedResponses')}
        </h3>
        <div className="space-y-6">
          {responses.map((response, index) => (
            <div key={index} className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <div className="font-medium text-gray-900">
                  Q{index + 1}: {response.question}
                </div>
              </div>
              
              <div className="p-4">
                <div className="text-gray-700 mb-4 whitespace-pre-wrap">
                  {response.answer}
                </div>

                {response.videoUrl && (
                  <div className="flex items-center gap-4 mb-4">
                    <a
                      href={response.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100"
                    >
                      <PlayIcon className="h-4 w-4" />
                      {t('interview.summary.watchResponse')}
                    </a>
                  </div>
                )}

                {response.feedback && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm text-gray-600 mb-2">
                      {response.feedback.feedback}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(response.feedback.score)}`}>
                        {t('interview.summary.questionScore')}: {response.feedback.score}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}